import React, { useEffect, useMemo, useState } from "react";
import styles from "./HeroSection.module.css";
import {
  HERO_HEADLINE_HTML,
  HERO_SUBTITLE_HTML,
  HERO_CTA_HTML,
} from "../../../content/marketingCopy";

/** detect reduced motion once */
const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

/* ---------- Graph rotator ---------- */
function GraphRotator() {
  const slides = useMemo(() => ["cashflow", "spendBars", "categoryDonut"], []);
  const [active, setActive] = useState(0);
  const [animSeed, setAnimSeed] = useState(0); // force remount of animated nodes

  useEffect(() => {
    if (prefersReduced()) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        setActive((i) => (i + 1) % slides.length);
      }
    }, 5000);
    return () => window.clearInterval(id);
  }, [slides.length]);

  useEffect(() => {
    // restart animations on slide change
    setAnimSeed((s) => s + 1);
  }, [active]);

  return (
    <div className={styles.mockCard} aria-hidden="true" tabIndex={-1}>
      <div className={styles.rotator}>
        {/* 1) Cashflow line */}
        <div className={`${styles.slide} ${active === 0 ? styles.active : ""}`}>
          <Cashflow key={`cash-${animSeed}`} />
          <div className={styles.captionRow}>
            <span className={styles.kpi}><strong>+8.2%</strong> cashflow MoM</span>
            <span className={styles.kpiMuted}>Jan → Sep</span>
          </div>
        </div>

        {/* 2) Monthly total bars */}
        <div className={`${styles.slide} ${active === 1 ? styles.active : ""}`}>
          <Bars key={`bars-${animSeed}`} seed={animSeed} />
          <div className={styles.captionRow}>
            <span className={styles.kpi}><strong>$2,940</strong> total monthly spend</span>
            <span className={styles.kpiMuted}>Top: Rent, Travel</span>
          </div>
        </div>

        {/* 3) Category mix donut */}
        <div className={`${styles.slide} ${active === 2 ? styles.active : ""}`}>
          <Donut key={`donut-${animSeed}`} />
          <div className={styles.legend} aria-hidden="true">
            <span><i className={`${styles.dotKey} ${styles.k1}`} /> Rent</span>
            <span><i className={`${styles.dotKey} ${styles.k2}`} /> Groceries</span>
            <span><i className={`${styles.dotKey} ${styles.k3}`} /> Dining</span>
            <span><i className={`${styles.dotKey} ${styles.k4}`} /> Travel</span>
            <span><i className={`${styles.dotKey} ${styles.k5}`} /> Utilities</span>
          </div>
          <div className={styles.captionRow}>
            <span className={styles.kpi}><strong>45%</strong> housing share</span>
            <span className={styles.kpiMuted}>Budget health: OK</span>
          </div>
        </div>
      </div>

      {/* pager */}
      <div className={styles.pager} aria-hidden="true">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`${styles.pageDot} ${active === i ? styles.pageDotActive : ""}`}
            onClick={() => setActive(i)}
            tabIndex={-1}
            aria-label={`Show slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ----- Cashflow (line + area) ----- */
function Cashflow() {
  return (
    <div className={styles.fill}>
      <svg
        className={styles.chartSvg}
        viewBox="0 0 600 260"
        preserveAspectRatio="none"
        role="img"
        aria-labelledby="ppLineTitle ppLineDesc"
      >
        <title id="ppLineTitle">Balance over time</title>
        <desc id="ppLineDesc">A smooth line chart trending upward across months.</desc>

        <defs>
          <linearGradient id="ppArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={`rgb(var(--pp-primary-rgb))`} stopOpacity="0.22" />
            <stop offset="100%" stopColor={`rgb(var(--pp-primary-rgb))`} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* grid */}
        <g className={styles.grid}>
          {[40, 90, 140, 190, 240].map((y) => (
            <line key={y} x1="50" y1={y} x2="580" y2={y} />
          ))}
        </g>

        {/* area first, then line */}
        <path
          key="area"
          className={styles.lineFill}
          d="M50,200 C120,180 190,160 260,150 C330,140 400,110 470,95 C510,86 540,78 580,72 L580,250 L50,250 Z"
          fill="url(#ppArea)"
        />
        <path
          key="line"
          className={styles.linePath}
          d="M50,200 C120,180 190,160 260,150 C330,140 400,110 470,95 C510,86 540,78 580,72"
          pathLength="1"
          fill="none"
          stroke={`rgb(var(--pp-primary-rgb))`}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/* ----- Bars (animate height, not transform) ----- */
function Bars({ seed = 0 }) {
  const data = [
    { v: 0.78, label: "Rent" },
    { v: 0.46, label: "Groceries" },
    { v: 0.32, label: "Dining" },
    { v: 0.58, label: "Travel" },
    { v: 0.40, label: "Utilities" },
    { v: 0.28, label: "Misc" },
  ];
  return (
    <div className={`${styles.fill} ${styles.barPanel}`}>
      <div className={styles.barRow}>
        {data.map((b, i) => (
          <span key={`${seed}-${b.label}-${i}`} className={styles.barWrap}>
            <span
              className={styles.bar}
              style={{
                ["--h"]: b.v,            // target height as fraction (0..1)
                ["--delay"]:`${i*70}ms` // stagger animation
              }}
            />
            <span className={styles.barLabel}>{b.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ----- Donut ----- */
function Donut() {
  const C = 289; // circumference for r=46
  const segments = [
    { p: 45, cls: "seg1" },
    { p: 20, cls: "seg2" },
    { p: 12, cls: "seg3" },
    { p: 15, cls: "seg4" },
    { p: 8,  cls: "seg5" },
  ];

  let acc = 0;
  return (
    <div className={`${styles.fill} ${styles.donutWrap}`}>
      <svg
        className={styles.donut}
        viewBox="0 0 120 120"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-labelledby="ppDonutTitle ppDonutDesc"
      >
        <title id="ppDonutTitle">Spending by category</title>
        <desc id="ppDonutDesc">A donut chart showing the share of spending by category.</desc>
        <g transform="translate(60,60) rotate(-90)">
          <circle r="46" className={styles.ringBg} />
          {segments.map((s, i) => {
            const len = (s.p / 100) * C;
            const offset = -acc;
            acc += len;
            return (
              <circle
                key={`${s.cls}-${i}`}
                r="46"
                className={`${styles.ring} ${styles[s.cls]}`}
                style={{
                  strokeDasharray: `0 ${C}`, // start hidden, animate to var(--segLen)
                  strokeDashoffset: `${offset}`,
                  ["--segDelay"]: `${i * 90}ms`,
                  ["--segLen"]: `${len}`,
                }}
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
}

/* ---------- Main hero ---------- */
export default function HeroSection() {
  return (
    <section
      id="hero"
      className={`py-5 py-lg-6 ${styles.hero}`}
      role="region"
      aria-labelledby="hero-heading"
    >
      <div className="container">
        <div className="row align-items-center g-5">
          <div className="col-12 col-lg-6 text-center text-lg-start">
            <h1
              id="hero-heading"
              className="display-4 fw-bold"
              dangerouslySetInnerHTML={{ __html: HERO_HEADLINE_HTML }}
            />
            <p
              id="hero-subtitle"
              className="lead mt-3"
              dangerouslySetInnerHTML={{ __html: HERO_SUBTITLE_HTML }}
            />
            <div
              className={`mt-4 ${styles.ctaWrap}`}
              aria-describedby="hero-subtitle"
              dangerouslySetInnerHTML={{ __html: HERO_CTA_HTML }}
            />
            <p className={`mt-3 mb-0 ${styles.betaNote}`}>
              Public Beta • Limited seats • Export anytime
            </p>
          </div>

          <div className="col-12 col-lg-6 text-center">
            <GraphRotator />
          </div>
        </div>
      </div>
    </section>
  );
}
